import { collection, FirestoreDataConverter } from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { firestore } from '@/lib/firebase';
import { useMemo } from 'react';

enum DeviceStatus {
  OK = 'OK',
  PING_FAIL = 'PING_FAIL',
  PING_SUCCESS = 'PING_SUCCESS',
}

export const BOX_BRANDS = ['NVIDIA', 'NEXUS', 'S905', '928X'] as const;
export type BoxBrand = (typeof BOX_BRANDS)[number];

/**
 * Behavioral family. S905 / 928X are Amlogic boxes that run the NVIDIA build,
 * so every behavioral branch (APK matching, capture quirks) tests
 * NEXUS-vs-others — never `=== 'NVIDIA'`, which would silently drop the
 * Amlogic brands onto the NEXUS side.
 */
export type BrandFamily = 'NVIDIA' | 'NEXUS';
/** APKs are builds, not chips — they are tagged by family, never by brand. */
export const BRAND_FAMILIES = ['NVIDIA', 'NEXUS'] as const;
export function brandFamily(brand: string): BrandFamily {
  return brand === 'NEXUS' ? 'NEXUS' : 'NVIDIA';
}

export type Box = {
  id?: string;
  name: string;
  ip: string;
  brand: string;
  status: DeviceStatus;
  androidVersion?: string;
  appVersion?: string;
  /** Config group from the device list (HDA / HDB / SMR / Replay). */
  group?: string;
  /** Which TV the box drives ("TV-1", "TV 04-Replay", "Left", ...). */
  tv?: string;
  /** 5th device-list field; empty today, carried for the headend feed. */
  icon?: string;
};

const boxConverter: FirestoreDataConverter<Box> = {
  toFirestore: (box) => {
    const data = { ...box };
    delete data.id;
    return data;
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return {
      id: snapshot.id,
      name: d.name,
      ip: d.ip,
      brand: d.brand,
      status: d.status,
      androidVersion: d.androidVersion,
      appVersion: d.appVersion,
      group: d.group,
      tv: d.tv,
      icon: d.icon,
    };
  },
};

export function useBoxes() {
  // Stable ref across renders. Note react-firebase-hooks already dedupes
  // equal refs via refEqual (converter is module-level, so an inline ref
  // compares equal) — this only avoids re-creating the object each render.
  const ref = useMemo(
    () => collection(firestore, 'boxes').withConverter(boxConverter),
    []
  );
  return useCollectionData(ref, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });
}

// Box mutations live in boxAudit.ts (auditedUpdateBox / auditedCreateBox /
// auditedDeleteBox) so every change is logged — no unaudited writers here.

/** Basic dotted-quad check — the agent pings/adb-connects this verbatim. */
export function isValidIp(value: string) {
  const parts = value.trim().split('.');
  return (
    parts.length === 4 &&
    parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255)
  );
}
